import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';
import OutcomeModal from './OutcomeModal';
import T2MNeedsProgrammingModal from './T2MNeedsProgrammingModal';
import T2MManagerConnectionModal from './T2MManagerConnectionModal';
import ContactLines from '../../components/ContactLines';

// ─── T2M Kanban (Sprint 21.3, 9/28/26) ────────────────────────────────────────
// Per docs/COMS_T2M_Kanban_Design_Decisions_2026-09-28.md — same design
// language as PB101/Retention/Lead Engine's Kanban redesigns, mirroring
// RetentionKanban.jsx's structure. One linear sequence of club- and sales-
// owned stages (mirrors PB101), reading GET /t2m/kanban (getT2MKanbanBoard,
// queries.js). Logs outcomes through the existing OutcomeModal — reused via
// its new optional `endpoint` prop rather than duplicated — pointed at the
// new FD-inclusive /t2m/kanban/outcome route. Does not touch
// T2MCheckpoint.jsx/T2MManager.jsx, which the existing /t2m view keeps
// using unchanged.

// `desc` = the stage's task definition, shown in the column header like PB101's (BK, 10/6/26: the
// Trial Pipeline was missing it). Wording follows docs/COMS_T2M_Kanban_Design_Decisions_2026-09-28.md.
const STAGE_META = {
  new_trial:         { label: 'New Trial', owner: 'Club', ownerColor: '#3b82f6',
                       desc: 'Just started. Welcome them, book their next visit, get them oriented.' },
  needs_programming: { label: 'Needs Programming Help', owner: 'Club', ownerColor: '#3b82f6',
                       desc: 'Club task list. Trials behind on visits, gone quiet 3+ days, or ending soon appear here as tasks (their card stays in its own column). Also holds notes passed to the club by Inside Sales.' },
  awaiting_contact:  { label: 'Trial Awaiting Contact', owner: 'Sales', ownerColor: '#a855f7',
                       desc: 'No outcome logged yet. Make the first contact.' },
  contacted:         { label: 'Awaiting Response', owner: 'Sales', ownerColor: '#a855f7',
                       desc: 'We reached out, no real conversation yet (no answer, voicemail, text or email sent). Try again if no reply in 5 days.' },
  engaged:           { label: 'Trial Engaged', owner: 'Sales', ownerColor: '#a855f7',
                       desc: 'Real exchange logged. Keep them on pace for visits and move toward the membership ask.' },
  follow_up:         { label: 'Follow Up Scheduled', owner: 'Sales', ownerColor: '#a855f7',
                       desc: 'A follow-up is on the calendar. Stays here until the next contact is logged; red once the date has passed.' },
  membership_asked:  { label: 'Membership Asked — Undecided', owner: 'Club, Manager, or Sales', ownerColor: '#f97316',
                       desc: 'The ask is made, no decision yet. Follow up until yes or no.' },
  sold:              { label: 'Sold', owner: 'Closed', ownerColor: '#22c55e', desc: 'Converted to a membership.' },
  soft_no:           { label: 'Soft No', owner: 'Closed', ownerColor: '#f97316', desc: 'Reason captured, follow-up scheduled.' },
  hard_no:           { label: 'Hard No', owner: 'Closed', ownerColor: '#6b7280', desc: 'Reason captured, no follow-up needed.' },
};

// Membership Asked sits with the club-owned stages, right after Needs Programming Help (BK, 10/6/26).
const COLUMN_ORDER = ['new_trial', 'needs_programming', 'membership_asked', 'awaiting_contact', 'contacted', 'engaged', 'follow_up', 'sold', 'soft_no', 'hard_no'];
const TERMINAL_STAGES = ['sold', 'soft_no', 'hard_no'];

// Groups inside the Needs Programming Help task list, by the reason a trial is there (BK, 10/23/26).
const TASK_GROUPS = [
  { key: 'behind', label: 'Behind on visits', color: '#f59e0b' },
  { key: 'quiet',  label: 'Gone quiet 3+ days', color: '#ef4444' },
  { key: 'ending', label: 'Ending soon, ask to join', color: '#22c55e' },
];

// Who owns each stretch of the board, shown as a band above the columns like the PB101 board (BK, 10/23/26: the Trial Pipeline
// should show what the club owns and what Inside Sales owns). Membership Asked stays with the club side (BK, 10/6/26); the manager or Inside Sales can pick it up.
const OWNER_GROUPS = [
  { key: 'club',    label: 'Club · Front Desk', color: '#eab308', stages: ['new_trial', 'needs_programming', 'membership_asked'] },
  { key: 'sales',   label: 'Inside Sales',      color: '#3b82f6', stages: ['awaiting_contact', 'contacted', 'engaged', 'follow_up'] },
  { key: 'outcome', label: 'Outcomes',          color: '#888',    stages: ['sold', 'soft_no', 'hard_no'] },
];

function Badge({ children, color }) {
  return (
    <span style={{
      background: `${color}22`, color, border: `1px solid ${color}`,
      fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
      textTransform: 'uppercase', letterSpacing: 0.4, marginRight: 4, display: 'inline-block', marginBottom: 4,
    }}>
      {children}
    </span>
  );
}

function FollowUpBadge({ card }) {
  if (!card.followUpDate) return null;
  // follow_up_date comes back from Postgres (and through JSON) as a full ISO
  // timestamp (e.g. "2026-10-01T00:00:00.000Z"), not a plain date string —
  // slice to the date portion before comparing/parsing, or a same-day due
  // date lexicographically "loses" to today's plain YYYY-MM-DD and appending
  // 'T12:00:00' to the full timestamp produces an Invalid Date.
  const dateOnly = String(card.followUpDate).slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const color = dateOnly <= today ? '#ef4444' : '#3b82f6';
  const label = new Date(dateOnly + 'T12:00:00').toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
  return <Badge color={color}>📅 Follow up {label}</Badge>;
}

// "4 visits by day 12" — the already-flagged visit-pace gap (backlog, Module:
// T2M), scaled to a 1-visit-per-3-days target and capped at the real day-12
// checkpoint, since that's the concrete number this was framed around.
function VisitPaceBadge({ card }) {
  const day = Math.min(card.trialDay || 1, 12);
  const target = Math.min(4, Math.ceil(day / 3));
  const visits = card.visits || 0;
  const color = visits >= target ? '#22c55e' : visits >= target - 1 ? '#f97316' : '#ef4444';
  return <Badge color={color}>🎾 {visits}/{target} visits (day {card.trialDay ?? '—'})</Badge>;
}

function ManagerConnectionBadge({ card, onClick }) {
  const status = card.managerConnectionStatus;
  if (!status) return null;
  if (status.done) {
    return <Badge color="#22c55e">🤝 Connected{status.managerName ? ` — ${status.managerName}` : ''}</Badge>;
  }
  if (status.due) {
    return (
      <span onClick={e => { e.stopPropagation(); onClick(); }} style={{ cursor: 'pointer' }}>
        <Badge color="#ef4444">🤝 Manager connection due</Badge>
      </span>
    );
  }
  return null;
}

function KanbanCard({ card, stageKey, onLogOutcome, onNeedsProgramming, onManagerConnection }) {
  const isTerminal = TERMINAL_STAGES.includes(stageKey);
  return (
    <div
      draggable={!isTerminal}
      onDragStart={!isTerminal ? (e) => {
        e.dataTransfer.setData('application/json', JSON.stringify({ id: card.id, stage: stageKey }));
        e.dataTransfer.effectAllowed = 'move';
      } : undefined}
      onClick={!isTerminal ? () => onLogOutcome(card) : undefined}
      style={{
        background: '#141414', border: '1px solid #2a2a2a', borderRadius: 7,
        padding: '7px 8px', marginBottom: 6,
        cursor: isTerminal ? 'default' : 'pointer',
      }}
    >
      <div style={{ fontWeight: 500, fontSize: 12, marginBottom: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {card.name}
      </div>
      <ContactLines phone={card.phone} email={card.email} name={card.name} clubId={card.clubId} size="xs" />

      <div style={{ marginBottom: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        <VisitPaceBadge card={card} />
        <FollowUpBadge card={card} />
        <ManagerConnectionBadge card={card} onClick={() => onManagerConnection(card)} />
        {stageKey === 'sold' && <Badge color="#22c55e">Sold</Badge>}
        {stageKey === 'soft_no' && <Badge color="#f97316">Soft No</Badge>}
        {stageKey === 'hard_no' && <Badge color="#6b7280">Hard No</Badge>}
      </div>

      {(card.skillLevel || card.duprRating) && (
        <div style={{ fontSize: 9.5, color: '#888', marginBottom: 4 }}>
          {card.duprRating ? `DUPR ${card.duprRating}` : `Skill: ${card.skillLevel}`}
        </div>
      )}

      {!isTerminal && (
        <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
          <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '4px 6px', flex: 1 }}
            onClick={e => { e.stopPropagation(); onLogOutcome(card); }}>
            Log outcome
          </button>
          <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '4px 6px' }}
            title="Flag for club programming help"
            onClick={e => { e.stopPropagation(); onNeedsProgramming(card); }}>
            🏓
          </button>
        </div>
      )}
    </div>
  );
}

// Where a card can be dragged. A trial's stage comes from the outcome logged on it (it feeds the
// contact history and commission checks), so a drop never moves a card by itself: it opens the log
// outcome form with the destination shown, and the card moves when the outcome is saved.
const DROP_TARGETS = ['contacted', 'engaged', 'follow_up', 'membership_asked', 'soft_no', 'hard_no'];

function Column({ stageKey, cards, onLogOutcome, onNeedsProgramming, onManagerConnection, onCardDrop, tasks = [], managerTasks = [] }) {
  const meta = STAGE_META[stageKey];
  const stageNum = COLUMN_ORDER.indexOf(stageKey) + 1;
  const [dragOver, setDragOver] = useState(false);
  const isDropTarget = DROP_TARGETS.includes(stageKey);
  return (
    <div
      style={{ flex: '0 0 172px', width: 172, outline: dragOver && isDropTarget ? '2px solid #eab308' : 'none', borderRadius: 8 }}
      onDragOver={isDropTarget ? (e) => { e.preventDefault(); setDragOver(true); } : undefined}
      onDragLeave={isDropTarget ? () => setDragOver(false) : undefined}
      onDrop={isDropTarget ? (e) => {
        e.preventDefault();
        setDragOver(false);
        try {
          const dragged = JSON.parse(e.dataTransfer.getData('application/json'));
          if (dragged.stage !== stageKey) onCardDrop(dragged.id, stageKey);
        } catch (err) { /* ignore malformed drag payload */ }
      } : undefined}
    >
      <div style={{ background: '#1c1c1c', borderRadius: 7, padding: '5px 7px', marginBottom: 6 }}>
        <span style={{
          float: 'right', fontFamily: 'monospace', fontSize: 10, fontWeight: 500,
          background: '#0a0a0a', color: '#a8a496', padding: '1px 6px', borderRadius: 20,
        }}>{cards.length}</span>
        <div style={{ fontWeight: 700, fontSize: 10.5, color: '#a8a496', lineHeight: 1.3 }}>
          {stageNum} · {meta.label}
        </div>
        <div style={{ marginTop: 3 }}>
          <span style={{
            fontSize: 8.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
            color: meta.ownerColor,
          }}>
            {meta.owner}
          </span>
        </div>
        {meta.desc && (
          <div style={{ fontSize: 9, color: '#888', marginTop: 4, lineHeight: 1.35 }}>{meta.desc}</div>
        )}
      </div>

      {stageKey === 'needs_programming' && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
            Tasks · {tasks.length + managerTasks.length}
          </div>
          {tasks.length === 0 && managerTasks.length === 0 && (
            <div style={{ fontSize: 10, color: '#555', padding: '6px 4px', border: '1px dashed #2a2a2a', borderRadius: 7 }}>
              No trials are behind, quiet, ending soon, or waiting on a manager connection.
            </div>
          )}
          {managerTasks.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: 0.4, margin: '6px 0 4px' }}>
                🤝 Manager connection due · {managerTasks.length}
              </div>
              {managerTasks.map(t => (
                <div key={`mgr_${t.id}`} onClick={() => onManagerConnection(t)} title="Opens the manager connection form"
                  style={{ background: '#15121f', border: '1px solid #33295a', borderRadius: 7, padding: '6px 8px', marginBottom: 5, cursor: 'pointer' }}>
                  <div style={{ fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    ☐ {t.name} <span style={{ color: '#a78bfa', fontWeight: 600, fontSize: 10.5 }}>· Day {t.trialDay}</span>
                  </div>
                  <div style={{ fontSize: 10, color: '#d4d0c4', lineHeight: 1.4 }}>
                    Halfway point {t.midpointDate ? new Date(t.midpointDate + 'T12:00:00').toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' }) : '—'}
                    {t.daysOver > 0 ? ` · ${t.daysOver}d over` : ' · due today'}
                  </div>
                  {(t.phone || t.email) && <div style={{ fontSize: 9.5, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.phone || t.email}</div>}
                </div>
              ))}
            </div>
          )}
          {TASK_GROUPS.map(g => {
            const list = tasks.filter(t => t.group === g.key);
            if (!list.length) return null;
            return (
              <div key={g.key} style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: g.color, textTransform: 'uppercase', letterSpacing: 0.4, margin: '6px 0 4px' }}>
                  {g.label} · {list.length}
                </div>
                {list.map(t => (
                  <div key={`task_${t.id}`} onClick={() => onLogOutcome(t)} title="Opens the same contact form as the trial card"
                    style={{ background: '#17140d', border: '1px solid #3a2f12', borderRadius: 7, padding: '6px 8px', marginBottom: 5, cursor: 'pointer' }}>
                    <div style={{ fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      ☐ {t.name} <span style={{ color: '#f59e0b', fontWeight: 600, fontSize: 10.5 }}>· Day {t.trialDay}</span>
                    </div>
                    <div style={{ fontSize: 10, color: '#d4d0c4', lineHeight: 1.4 }}>
                      {t.visits >= t.paceTarget ? `${t.visits} visits · on pace` : `${t.visits} of ${t.paceTarget} visits`}
                      {t.lastVisitDate ? ` · last in ${t.lastVisitDate}` : ' · no visit yet'}
                    </div>
                    <div style={{ marginTop: 2 }}>
                      {t.isBehind && <Badge color="#f59e0b">{t.behindBy} behind</Badge>}
                      {t.isQuiet && <Badge color="#ef4444">Quiet {t.quietDays}d</Badge>}
                      {t.isEnding && <Badge color="#22c55e">{t.daysLeft === 0 ? 'Ends today' : `Ends in ${t.daysLeft}d`} · ask to join</Badge>}
                    </div>
                    {(t.phone || t.email) && <div style={{ fontSize: 9.5, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.phone || t.email}</div>}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
      {cards.length === 0 && (
        <div style={{ fontSize: 10, color: '#555', textAlign: 'center', padding: '12px 4px', border: '1px dashed #2a2a2a', borderRadius: 7 }}>
          Nothing here
        </div>
      )}
      {cards.map(c => (
        <KanbanCard
          key={c.id}
          card={c}
          stageKey={stageKey}
          onLogOutcome={onLogOutcome}
          onNeedsProgramming={onNeedsProgramming}
          onManagerConnection={onManagerConnection}
        />
      ))}
    </div>
  );
}

export default function T2MKanban() {
  const { get } = useApi();
  const { currentClub } = useClub();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [outcomeTarget, setOutcomeTarget] = useState(null);
  const [programmingTarget, setProgrammingTarget] = useState(null);
  const [managerTarget, setManagerTarget] = useState(null);

  const load = useCallback(() => {
    if (!currentClub?.id) { setLoading(false); return; }
    setLoading(true);
    setLoadError('');
    get(`/t2m/kanban?club=${currentClub.id}`)
      .then(setBoard)
      .catch(e => setLoadError(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [currentClub?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;
  if (loadError) return (
    <div className="empty-state">
      <p>Couldn't load the trial pipeline: {loadError}</p>
    </div>
  );
  if (!board) return <div className="empty-state"><p>No data.</p></div>;

  const total = Object.values(board.counts || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="module-page kanban-page">
      <div className="module-header" style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: 22 }}>🌱</span>
          <h1 className="module-title">Trial Pipeline</h1>
        </div>
        <div style={{ fontSize: 12.5, color: '#888' }}>
          {currentClub?.name || ''} · {total} active
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8, alignItems: 'flex-start' }}>
        {OWNER_GROUPS.map(group => (
          <div key={group.key} style={{ flex: 'none' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '3px 2px 5px',
              marginBottom: 6, borderBottom: `2px solid ${group.color}`,
            }}>
              <span style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: group.color }}>
                {group.label}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {group.stages.map(stageKey => (
                <Column
                  key={stageKey}
                  stageKey={stageKey}
                  cards={board.stages?.[stageKey] || []}
                  onLogOutcome={setOutcomeTarget}
                  onNeedsProgramming={setProgrammingTarget}
                  onManagerConnection={setManagerTarget}
                  tasks={board.clubTasks || []}
                  managerTasks={board.managerTasks || []}
                  onCardDrop={(id, toStage) => {
                    const card = Object.values(board.stages || {}).flat().find(c => c.id === id);
                    if (card) setOutcomeTarget({ ...card, movingTo: STAGE_META[toStage]?.label || toStage });
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {outcomeTarget && (
        <OutcomeModal
          trial={outcomeTarget}
          endpoint="/t2m/kanban/outcome"
          onClose={() => setOutcomeTarget(null)}
          onSaved={() => { setOutcomeTarget(null); load(); }}
        />
      )}
      {programmingTarget && (
        <T2MNeedsProgrammingModal
          card={programmingTarget}
          onClose={() => setProgrammingTarget(null)}
          onSaved={() => { setProgrammingTarget(null); load(); }}
        />
      )}
      {managerTarget && (
        <T2MManagerConnectionModal
          card={managerTarget}
          onClose={() => setManagerTarget(null)}
          onSaved={() => { setManagerTarget(null); load(); }}
        />
      )}
    </div>
  );
}
